import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@suscri/ui';
import { Plus, Search, Tag, X, Edit, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '../components/ui/PageHeader';
import { trpc } from '../lib/trpc';
import type { RouterOutputs } from '../lib/trpc';

const buildCategorySchema = (t: (k: string) => string) =>
  z.object({
    name: z.string().min(1, t('El nombre es requerido')),
    color: z.string().min(1, t('El color es requerido')),
    description: z.string().optional(),
  });

type CreateCategoryForm = z.infer<ReturnType<typeof buildCategorySchema>>;
type CategoryListItem = RouterOutputs['categories']['list']['data'][number];

export function CategoriesPage() {
  const { t } = useTranslation();
  const createCategorySchema = useMemo(() => buildCategorySchema(t), [t]);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<CategoryListItem | null>(null);

  const { data, isLoading, refetch } = trpc.categories.list.useQuery({
    search: search || undefined,
    pagination: { page: 1, limit: 20 },
  });

  const createCategoryMutation = trpc.categories.create.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      refetch();
    },
  });

  const updateCategoryMutation = trpc.categories.update.useMutation({
    onSuccess: () => {
      setIsEditModalOpen(false);
      setEditingCategory(null);
      refetch();
    },
  });

  const deleteCategoryMutation = trpc.categories.delete.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateCategoryForm>({
    resolver: zodResolver(createCategorySchema),
  });

  const onSubmit = async (data: CreateCategoryForm) => {
    await createCategoryMutation.mutateAsync(data);
    reset();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const handleEditCategory = (category: CategoryListItem) => {
    setEditingCategory(category);
    setIsEditModalOpen(true);
    reset({
      name: category.name,
      color: category.color || '#6B7280',
      description: category.description || '',
    });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCategory(null);
    reset();
  };

  const handleUpdateCategory = async (data: CreateCategoryForm) => {
    if (!editingCategory) return;

    await updateCategoryMutation.mutateAsync({
      id: editingCategory.id,
      data,
    });
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (confirm(t('¿Estás seguro de que quieres eliminar esta categoría?'))) {
      await deleteCategoryMutation.mutateAsync(categoryId);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('Organización')}
        title={t('Categorías')}
        description={t('Organiza tus suscripciones por categorías')}
        actions={<Button onClick={() => setIsModalOpen(true)}><Plus className="h-4 w-4" />{t('Nueva Categoría')}</Button>}
      />

      <div className="surface-card flex items-center p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('Buscar categorías...')}
            className="control w-full pl-10 pr-4"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-md" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data?.data.map(category => (
            <div
              key={category.id}
              onClick={() => handleEditCategory(category)}
              className="surface-card-interactive group relative cursor-pointer overflow-hidden p-6"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-md"
                      style={{ backgroundColor: category.color || '#6B7280' }}
                    >
                      <Tag className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('{{count}} suscripciones', {
                          count: category._count.subscriptions,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="p-2 text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('Editar categoría')}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="p-2 text-gray-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('Eliminar categoría')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">
                {t('Creada:')}{' '}
                {new Date(category.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para crear categoría */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          <div className="relative w-full max-w-md">
            <div className="overflow-hidden rounded-lg bg-white border border-gray-200 shadow-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('Nueva Categoría')}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Nombre *')}
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Ej: Entretenimiento, Productividad...')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Color *')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      className="w-12 h-10 border border-input rounded cursor-pointer"
                      value={watch('color') || '#6B7280'}
                      onChange={e => {
                        setValue('color', e.target.value);
                      }}
                    />
                    <input
                      {...register('color')}
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="#6B7280"
                      onChange={e => {
                        setValue('color', e.target.value);
                      }}
                    />
                  </div>
                  {errors.color && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.color.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Descripción')}
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Descripción de la categoría...')}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {t('Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={createCategoryMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {createCategoryMutation.isPending
                      ? t('Creando...')
                      : t('Crear Categoría')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar categoría */}
      {isEditModalOpen && editingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseEditModal}
          />
          <div className="relative w-full max-w-md">
            <div className="overflow-hidden rounded-lg bg-white border border-gray-200 shadow-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('Editar Categoría')}
                </h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleSubmit(handleUpdateCategory)}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Nombre *')}
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Ej: Entretenimiento, Productividad...')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Color *')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      className="w-12 h-10 border border-input rounded cursor-pointer"
                      value={watch('color') || '#6B7280'}
                      onChange={e => {
                        setValue('color', e.target.value);
                      }}
                    />
                    <input
                      {...register('color')}
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="#6B7280"
                      onChange={e => {
                        setValue('color', e.target.value);
                      }}
                    />
                  </div>
                  {errors.color && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.color.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Descripción')}
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Descripción de la categoría...')}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseEditModal}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {t('Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateCategoryMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {updateCategoryMutation.isPending
                      ? t('Guardando...')
                      : t('Guardar Cambios')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
